import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, Plus } from 'lucide-react';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import ErrorAlert from '@/components/ui/ErrorAlert';
import DatePicker from '@/components/ui/DatePicker';
import { joinWaitlist, extractLandingErrorCode } from '@/lib/api/public';
import { getWaitlistError } from '@/lib/errors/waitlistErrors';
import type { FriendlyError } from '@/lib/errors/types';
import { captureFriendlyError } from '@/lib/sentry';
import { track } from '@/lib/analytics';

const ease = [0.22, 1, 0.36, 1] as const;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function WaitlistSection() {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [interviewDate, setInterviewDate] = useState('');
  const [showDateField, setShowDateField] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<FriendlyError | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  const isValidEmail = EMAIL_REGEX.test(email.trim());
  const canSubmit = isValidEmail && firstName.trim() !== '' && lastName.trim() !== '';

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    track('waitlist_signup_submitted', { source: 'final_cta' });
    try {
      const res = await joinWaitlist({
        email: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        interview_date: interviewDate || undefined,
      });
      setSucceeded(true);
      track('waitlist_signup_succeeded', {
        source: 'final_cta',
        already_subscribed: res.already_subscribed,
      });
    } catch (err) {
      const code = extractLandingErrorCode(err, 'waitlist_join_failed');
      const friendly = getWaitlistError(code);
      captureFriendlyError(err, friendly, { source: 'final_cta' });
      setError(friendly);
      track('waitlist_signup_failed', { source: 'final_cta', error_code: code });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="waitlist" className="relative bg-cream-warm py-14 md:py-24">
      <div className="mx-auto w-full max-w-2xl px-6 md:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-15%' }}
          transition={{ duration: 0.7, ease }}
        >
          <p className="font-display font-light text-sm text-text-secondary mb-4 flex items-center gap-3">
            <span className="tabular-nums">03</span>
            <span className="w-8 h-px bg-text-muted/50" />
            <span>On the list</span>
          </p>

          <h2 className="font-display font-light text-text-primary text-3xl md:text-5xl leading-[1.1] tracking-[-0.01em] mb-4 max-w-[18ch]">
            Save your spot. We'll write the day it's live.
          </h2>

          <p className="font-sans text-base text-text-secondary max-w-[48ch] leading-relaxed mb-6">
            Your name and email. Interview date is optional. It helps us
            look out for you.
          </p>

          <div className="rounded-3xl bg-card border border-border p-5 md:p-8 shadow-[0_1px_2px_rgba(45,36,22,0.04),0_8px_32px_-12px_rgba(45,36,22,0.12)]">
            <AnimatePresence mode="wait" initial={false}>
              {succeeded ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3, ease }}
                  className="text-center py-10 sm:py-12"
                >
                  <div className="relative w-16 h-16 mx-auto mb-5">
                    <div className="absolute inset-0 rounded-full bg-success/10" />
                    <motion.div
                      initial={{ scale: 0.6, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.4, delay: 0.1, ease }}
                      className="absolute inset-0 rounded-full bg-success/10 flex items-center justify-center"
                    >
                      <CheckCircle2 size={28} className="text-success" />
                    </motion.div>
                  </div>
                  <p className="font-display font-semibold text-2xl text-text-primary mb-2">
                    You're on the list
                  </p>
                  <p className="font-sans text-sm text-text-secondary max-w-[32ch] mx-auto leading-relaxed">
                    We'll email you the moment it's ready — no need to do anything else.
                  </p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3, ease }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    void submit();
                  }}
                  noValidate
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      id="waitlist-first-name"
                      label="First name"
                      value={firstName}
                      onChange={setFirstName}
                      placeholder="Ben"
                      disabled={submitting}
                    />
                    <Input
                      id="waitlist-last-name"
                      label="Last name"
                      value={lastName}
                      onChange={setLastName}
                      placeholder="Diaz"
                      disabled={submitting}
                    />
                  </div>

                  <div className="mt-4">
                    <Input
                      id="waitlist-email"
                      label="Email"
                      type="email"
                      value={email}
                      onChange={setEmail}
                      placeholder="you@example.com"
                      disabled={submitting}
                    />
                  </div>

                  <div className="mt-4">
                    <AnimatePresence initial={false} mode="wait">
                      {showDateField ? (
                        <motion.div
                          key="date-field"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          transition={{ duration: 0.35, ease }}
                          style={{ overflow: 'hidden' }}
                        >
                          <DatePicker
                            label="Interview date (optional)"
                            value={interviewDate || null}
                            onChange={setInterviewDate}
                          />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="date-toggle"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowDateField(true)}
                          >
                            <Plus size={14} />
                            Add interview date (optional)
                          </Button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {error && <ErrorAlert error={error} className="mt-4" />}

                  <div className="mt-6">
                    <Button
                      type="submit"
                      fullWidth
                      disabled={!canSubmit}
                      loading={submitting}
                    >
                      Notify me when it's ready
                    </Button>
                  </div>

                  <p className="font-sans text-sm text-text-secondary mt-4 text-center">
                    Free forever. No credit card. No spam — we'll only write when it's ready.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
